import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Lock, UserCheck, UserPlus, Loader2 } from "lucide-react";
import { usePrivatePage } from "../lib/seo";
import { Header } from "../components/Header";
import { ShieldPreview } from "../components/shield/ShieldPreview";
import { ParentEditor } from "../components/parent/ParentEditor";
import { api, errMsg, parentTokens, saveParentToken, forgetParentToken } from "../lib/api";

const PlayerTile = ({ p, mine, onPick }) => (
  <div className={`border-2 bg-white p-3 ${mine ? "border-slate-900 shadow-[3px_3px_0_0_#0f172a]" : "border-slate-200"}`} data-testid={`share-player-${p.id}`}>
    <div className="aspect-[406/650] w-full"><ShieldPreview design={{ ...p.design, guide: false }} testId={`share-shield-${p.id}`} /></div>
    <div className="mt-2 truncate font-bold">{p.title}</div>
    {mine ? (
      <button type="button" onClick={onPick} className="btn-blaze mt-2 w-full px-3 py-1.5 text-xs" data-testid={`share-continue-${p.id}`}>Continue editing</button>
    ) : p.claimed ? (
      <div className="mt-2 flex items-center gap-1 text-xs text-slate-500" data-testid={`share-locked-${p.id}`}><Lock className="h-3 w-3" /> Another parent is editing</div>
    ) : (
      <button type="button" onClick={onPick} className="mt-2 flex w-full items-center justify-center gap-1 border-2 border-slate-900 px-3 py-1.5 text-xs font-bold uppercase hover:bg-slate-900 hover:text-white" data-testid={`share-claim-${p.id}`}>
        <UserCheck className="h-3.5 w-3.5" /> This is my child
      </button>
    )}
  </div>
);

export default function ParentShare() {
  usePrivatePage("Parent page");
  const { code } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [active, setActive] = useState(null);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [email, setEmail] = useState(localStorage.getItem("ss_parent_email") || "");
  const parentEmail = () => { const e = email.trim(); if (e) localStorage.setItem("ss_parent_email", e); return e || null; };
  const tokens = parentTokens();
  const [params, setParams] = useSearchParams();

  const load = () => api.get(`/share/${code}`).then((r) => setData(r.data)).catch((e) => { toast.error(errMsg(e)); setData(false); });
  useEffect(() => { load(); }, [code]); // eslint-disable-line react-hooks/exhaustive-deps

  // Personal link from a reminder email: swap the one-time code for edit access on this device.
  useEffect(() => {
    const p = params.get("p"), r = params.get("r");
    if (!p || !r || !data) return;
    setParams({}, { replace: true });
    api.post(`/share/${code}/players/${p}/resume`, { token: r })
      .then(({ data: res }) => { saveParentToken(p, res.token); const pl = data.players.find((x) => x.id === p); if (pl) setActive({ ...pl, claimed: true }); })
      .catch((e) => toast.error(errMsg(e)));
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  const pick = async (p) => {
    if (tokens[p.id] && p.claimed) return setActive(p);
    try {
      const { data: r } = await api.post(`/share/${code}/players/${p.id}/claim`, { parent_email: parentEmail() });
      saveParentToken(p.id, r.token);
      setActive(p);
    } catch (e) { toast.error(errMsg(e)); load(); }
  };

  const addChild = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data: r } = await api.post(`/share/${code}/players`, { name: name.trim(), parent_email: parentEmail() });
      saveParentToken(r.player.id, r.token);
      setName("");
      setActive(r.player);
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  if (data === false) return <div className="min-h-screen"><Header onNew={() => nav("/design")} /><p className="p-10 text-center text-slate-500" data-testid="share-invalid">This squad link isn't valid – ask your coach for a new one.</p></div>;
  if (!data) return <div className="min-h-screen"><Header onNew={() => nav("/design")} /><p className="p-10 text-center text-slate-500">Loading…</p></div>;
  if (active) return <ParentEditor code={code} squad={data} player={active} token={parentTokens()[active.id]} onBack={() => { setActive(null); load(); }} onLost={() => { forgetParentToken(active.id); setActive(null); load(); }} />;

  const isMine = (p) => tokens[p.id] && p.claimed;
  const mine = data.players.filter(isMine);
  const others = data.players.filter((p) => !isMine(p));
  return (
    <div className="min-h-screen pb-10">
      <Header onNew={() => nav("/design")} />
      <main className="mx-auto max-w-6xl px-4 py-6 sm:px-6 lg:py-10">
        {data?.closed && <p className="mb-4 border-2 border-red-500 bg-red-50 p-3 text-sm font-bold text-red-700" data-testid="parent-fundraiser-closed">This squad's fundraiser has closed, so new orders aren't being taken. Please contact your coach.</p>}
        <div className="eyebrow">Parent page</div>
        <h1 className="font-display text-4xl font-black uppercase leading-none tracking-tight sm:text-5xl" data-testid="share-team-name">{data.team_name}</h1>
        <p className="mt-2 max-w-xl text-sm text-slate-600">Find your child, add their photo{data.allow_parent_stats ? " and statistics" : ""}, then order and pay for their shield. The club design is set by your coach.</p>
        <label className="mt-6 block max-w-md text-sm font-bold">Your email <span className="font-normal text-slate-500">(optional – so your coach can send you reminders)</span>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" data-testid="share-parent-email-input"
            className="mt-1 w-full border-2 border-slate-200 px-3 py-2 text-sm font-semibold outline-none focus:border-slate-900" />
        </label>
        {mine.length > 0 && (
          <section className="mt-8"><div className="eyebrow mb-3">My child's shield</div>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">{mine.map((p) => <PlayerTile key={p.id} p={p} mine onPick={() => pick(p)} />)}</div>
          </section>
        )}
        <section className="mt-8"><div className="eyebrow mb-3">Find your child</div>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4 lg:grid-cols-5">{others.map((p) => <PlayerTile key={p.id} p={p} onPick={() => pick(p)} />)}</div>
          {others.length === 0 && <p className="text-sm text-slate-500">No other players listed.</p>}
        </section>
        <form onSubmit={addChild} className="mt-8 flex max-w-lg flex-wrap items-end gap-2 border-2 border-dashed border-slate-300 bg-white p-4">
          <label className="min-w-0 flex-1 text-sm font-bold">My child isn't listed
            <input value={name} onChange={(e) => setName(e.target.value)} maxLength={40} required placeholder="Child's name" data-testid="share-add-child-input"
              className="mt-1 w-full border-2 border-slate-200 px-3 py-2 text-sm font-semibold outline-none focus:border-slate-900" />
          </label>
          <button type="submit" disabled={busy} className="btn-blaze flex items-center gap-2 px-4 py-2.5 text-sm" data-testid="share-add-child-button">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />} Add my child
          </button>
        </form>
      </main>
    </div>
  );
}
